import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, Paper, TextField, Button, Alert,
    IconButton, Fade, Snackbar, useTheme
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';

const ModuloCriarPagina = () => {
    const navegar = useNavigate();
    const theme = useTheme();
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setSucesso('');

        if (!nome || !dataInicio || !dataFim) {
            setErro('Por favor, preencha todos os campos obrigatórios (Nome, Data de Início, Data de Fim).');
            return;
        }

        try {
            const dadosDoModulo = {
                nome,
                descricao,
                data_inicio: dataInicio,
                data_fim: dataFim
            };

            await api.post('/api/modulos/criar', dadosDoModulo);

            setSucesso('Módulo criado com sucesso!');

            setTimeout(() => {
                navegar('/gerenciar-modulos');
            }, 1500);

        } catch (err) {
            setErro(err.response?.data?.erro || 'Ocorreu um erro ao criar o módulo.');
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 3,
                    boxShadow: cores.sombras.medio
                }}>
                    {/* Header azul */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar-modulos')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Criar Novo Módulo
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Preencha os dados para criar um novo módulo no sistema
                            </Typography>
                        </Box>
                    </Box>

                    {/* Formulário */}
                    <Box
                        component="form"
                        onSubmit={lidarComSubmissao}
                        sx={{
                            p: { xs: 0, sm: 2 },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <TextField
                            label="Nome do Módulo"
                            variant="outlined"
                            fullWidth
                            required
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            InputProps={{
                                sx: { borderRadius: 2 }
                            }}
                            helperText="Digite um nome claro e conciso para o módulo"
                        />
                        <TextField
                            label="Descrição"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            InputProps={{
                                sx: { borderRadius: 2 }
                            }}
                            helperText="Descreva o objetivo e o conteúdo deste módulo (opcional)"
                        />

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            width: '100%'
                        }}>
                            <TextField
                                label="Data de Início"
                                type="date"
                                variant="outlined"
                                fullWidth
                                required
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                                helperText="Quando este módulo será disponibilizado"
                            />
                            <TextField
                                label="Data de Fim"
                                type="date"
                                variant="outlined"
                                fullWidth
                                required
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                                helperText="Quando este módulo será encerrado"
                            />
                        </Box>

                        {erro && (
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    backgroundColor: cores.erro.claro,
                                    borderColor: cores.erro.medio,
                                    color: cores.erro.escuro,
                                    '& .MuiAlert-icon': {
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                {erro}
                            </Alert>
                        )}

                        <Box sx={{
                            mt: 4,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Button
                                variant="outlined"
                                onClick={() => navegar('/gerenciar-modulos')}
                                sx={{
                                    borderRadius: 2,
                                    py: 1.5,
                                    px: 3,
                                    borderColor: cores.cinza.medio,
                                    color: cores.cinza.escuro,
                                    '&:hover': {
                                        borderColor: cores.cinza.escuro,
                                        bgcolor: 'rgba(71, 85, 105, 0.04)'
                                    }
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                sx={{
                                    borderRadius: 2,
                                    py: 1.5,
                                    px: 3,
                                    bgcolor: cores.primaria.principal,
                                    fontWeight: 600,
                                    boxShadow: cores.sombras.suave,
                                    '&:hover': {
                                        bgcolor: cores.primaria.escuro,
                                    }
                                }}
                            >
                                Salvar Módulo
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Fade>

            <Snackbar
                open={!!sucesso}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: cores.sucesso.principal
                    }}
                >
                    {sucesso}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ModuloCriarPagina;